import { getSupabaseClient } from '../../lib/supabase'
import type { Database } from '../../types/database.generated'

export type Role = Database['public']['Enums']['organization_role']

export async function getTeamWorkspace(organizationId: string, includeAdmin: boolean) {
  const supabase = getSupabaseClient()
  const [members, organization] = await Promise.all([
    supabase.from('organization_members').select('*').eq('organization_id', organizationId),
    supabase.from('organizations').select('*').eq('id', organizationId).single(),
  ])
  if (members.error) throw members.error
  if (organization.error) throw organization.error
  const profiles = await supabase.from('profiles').select('id,display_name').in('id', members.data.map((member) => member.user_id))
  if (profiles.error) throw profiles.error
  let invitationRows: Database['public']['Tables']['organization_invitations']['Row'][] = []
  let auditRows: Database['public']['Tables']['admin_audit_log']['Row'][] = []
  if (includeAdmin) {
    const [invitations, audit] = await Promise.all([
      supabase.from('organization_invitations').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }),
      supabase.from('admin_audit_log').select('*').eq('organization_id', organizationId).order('created_at', { ascending: false }).limit(50),
    ])
    if (invitations.error) throw invitations.error
    if (audit.error) throw audit.error
    invitationRows = invitations.data
    auditRows = audit.data
  }
  return {
    organization: organization.data,
    members: members.data.map((member) => ({ ...member, displayName: profiles.data.find((profile) => profile.id === member.user_id)?.display_name ?? `Member ${member.user_id.slice(0, 8)}` })),
    invitations: invitationRows,
    audit: auditRows,
  }
}

export async function inviteMember(organizationId: string, email: string, role: Role) {
  const result = await getSupabaseClient().functions.invoke<{ error?: string }>('invite-member', { body: { organizationId, email, role, redirectTo: window.location.origin } }) as unknown as { data: { error?: string } | null; error: Error | null }
  if (result.error) {
    const response = (result.error as Error & { context?: Response }).context
    if (response) {
      const body = await response.clone().json().catch(() => null) as { error?: string } | null
      if (body?.error) throw new Error(body.error)
    }
    throw result.error
  }
  if (result.data?.error) throw new Error(result.data.error)
}
export async function changeMemberRole(organizationId: string, userId: string, role: Role) { const { error } = await getSupabaseClient().from('organization_members').update({ role }).eq('organization_id', organizationId).eq('user_id', userId); if (error) throw error }
export async function removeMember(organizationId: string, userId: string) { const { error } = await getSupabaseClient().from('organization_members').delete().eq('organization_id', organizationId).eq('user_id', userId); if (error) throw error }
export async function revokeInvitation(id: string) { const { error } = await getSupabaseClient().from('organization_invitations').update({ status: 'revoked' }).eq('id', id); if (error) throw error }
export async function updateOrganization(id: string, name: string, slug: string) { const { error } = await getSupabaseClient().from('organizations').update({ name: name.trim(), slug: slug.trim().toLowerCase() }).eq('id', id); if (error) throw error }
export async function updateProfile(id: string, displayName: string) { const { error } = await getSupabaseClient().from('profiles').update({ display_name: displayName.trim() || null }).eq('id', id); if (error) throw error }

export async function getOnboardingFunnel(organizationId: string) {
  const { data, error } = await getSupabaseClient().from('onboarding_funnel_summary').select('*').eq('organization_id', organizationId).maybeSingle()
  if (error) throw error
  return data ?? { organization_id: organizationId, registered: 0, email_confirmed: 0, organization_created: 0, property_created: 0, first_task_created: 0 }
}
